import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { 
  X, UserPlus, Sparkles, PenLine, RefreshCw, Save, Users, Blend
} from "lucide-react";
import { toast } from "sonner";

const AddPersonaModal = ({ isOpen, onClose, existingMatches = [] }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState("choose"); // choose, generate, preview
  const [prompt, setPrompt] = useState("");
  const [seed, setSeed] = useState("na");
  const [meshSelections, setMeshSelections] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedPersona, setGeneratedPersona] = useState(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep("choose");
      setPrompt("");
      setSeed("na");
      setMeshSelections([]);
      setGenerating(false);
      setGeneratedPersona(null);
    }
  }, [isOpen]);

  const handleManualEntry = () => {
    onClose();
    navigate("/relationships/new");
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a description for the persona");
      return;
    }

    setGenerating(true);
    try {
      const response = await authAxios.post("/ai/generate-persona", {
        prompt: prompt.trim(),
        seed: seed,
        mesh_ids: meshSelections
      });
      
      setGeneratedPersona(response.data);
      setStep("preview");
    } catch (firstError) {
      // Retry once — covers token refresh race after long LLM calls
      try {
        const response = await authAxios.post("/ai/generate-persona", {
          prompt: prompt.trim(),
          seed: seed,
          mesh_ids: meshSelections
        });
        setGeneratedPersona(response.data);
        setStep("preview");
      } catch (retryError) {
        console.error("Error generating persona:", retryError);
        toast.error("Failed to generate persona. Please try again.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleRegen = () => {
    handleGenerate();
  };

  const handleSave = () => {
    // Navigate to editor with generated data
    onClose();
    navigate("/relationships/new", { 
      state: { generatedPersona: generatedPersona }
    });
  };

  const toggleMeshSelection = (matchId) => {
    setMeshSelections(prev => 
      prev.includes(matchId) 
        ? prev.filter(id => id !== matchId)
        : [...prev, matchId]
    );
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-divine-black-light border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <h2 className="font-cinzel text-xl text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-divine-gold" />
              {step === "choose" && "Add New Persona"}
              {step === "generate" && "Generate Persona"}
              {step === "preview" && "Preview Generated Persona"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white/60" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {/* Step 1: Choose Method */}
            {step === "choose" && (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={handleManualEntry}
                  className="p-6 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-divine-gold/50 rounded-xl transition-all group"
                >
                  <PenLine className="w-10 h-10 text-divine-gold mb-4 mx-auto group-hover:scale-110 transition-transform" />
                  <h3 className="font-cinzel text-lg text-white mb-2">Manual Entry</h3>
                  <p className="text-white/60 text-sm">
                    Create a persona by filling out the profile form manually
                  </p>
                </button>

                <button
                  onClick={() => setStep("generate")}
                  className="p-6 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-xl transition-all group"
                >
                  <Sparkles className="w-10 h-10 text-purple-400 mb-4 mx-auto group-hover:scale-110 transition-transform" />
                  <h3 className="font-cinzel text-lg text-white mb-2">Generate with AI</h3>
                  <p className="text-white/60 text-sm">
                    Describe a persona and let AI create detailed profile
                  </p>
                </button>
              </div>
            )}

            {/* Step 2: Generate Options */}
            {step === "generate" && (
              <div className="space-y-6">
                {/* Prompt Input */}
                <div>
                  <Label className="text-white mb-2 block">Describe the persona you want to create</Label>
                  <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="e.g., A 28-year-old yoga instructor named Maya who loves hiking, is introverted but warm, values deep conversations, and has a playful sense of humor..."
                    className="min-h-[100px] bg-white/5 border-white/20 text-white placeholder:text-white/40"
                  />
                </div>

                {/* Seed Dropdown */}
                <div>
                  <Label className="text-white mb-2 block flex items-center gap-2">
                    <Users className="w-4 h-4 text-divine-gold" />
                    Seed (Base Profile)
                  </Label>
                  <Select value={seed} onValueChange={setSeed}>
                    <SelectTrigger className="bg-white/5 border-white/20 text-white">
                      <SelectValue placeholder="Select seed profile" />
                    </SelectTrigger>
                    <SelectContent className="bg-divine-black border-white/20">
                      <SelectItem value="na" className="text-white hover:bg-white/10">
                        N/A - Generate from scratch
                      </SelectItem>
                      <SelectItem value="user" className="text-white hover:bg-white/10">
                        User - Use my profile as base
                      </SelectItem>
                      {existingMatches.map((match) => (
                        <SelectItem 
                          key={match.match_id} 
                          value={match.match_id}
                          className="text-white hover:bg-white/10"
                        >
                          {match.name} - Use as base
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-white/40 text-xs mt-1">
                    The seed provides foundational traits for the generated persona
                  </p>
                </div>

                {/* Mesh Options */}
                {existingMatches.length > 0 && (
                  <div>
                    <Label className="text-white mb-2 block flex items-center gap-2">
                      <Blend className="w-4 h-4 text-purple-400" />
                      MESH (Blend Multiple Personas)
                    </Label>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-3 max-h-[200px] overflow-y-auto">
                      {existingMatches.map((match) => (
                        <div 
                          key={match.match_id}
                          className="flex items-center gap-3 py-2 px-2 hover:bg-white/5 rounded-lg cursor-pointer"
                          onClick={() => toggleMeshSelection(match.match_id)}
                          data-testid={`mesh-row-${match.match_id}`}
                        >
                          <Checkbox
                            checked={meshSelections.includes(match.match_id)}
                            onCheckedChange={() => toggleMeshSelection(match.match_id)}
                            onClick={(e) => e.stopPropagation()}
                            className="border-white/30 data-[state=checked]:bg-purple-500 data-[state=checked]:border-purple-500"
                            data-testid={`mesh-checkbox-${match.match_id}`}
                          />
                          <div className="w-8 h-8 rounded-full bg-divine-red/20 flex items-center justify-center overflow-hidden">
                            {(match.thumbnail_url || match.picture_url || match.picture) ? (
                              <img src={match.thumbnail_url || match.picture_url || match.picture} alt={match.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-divine-gold text-sm">{match.name[0]}</span>
                            )}
                          </div>
                          <span className="text-white text-sm">{match.name}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-white/40 text-xs mt-1">
                      Select personas to blend their traits into the generated persona
                    </p>
                  </div>
                )}

                {/* Generate Button */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setStep("choose")}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleGenerate}
                    disabled={generating || !prompt.trim()}
                    className="flex-1 bg-purple-600 hover:bg-purple-500 text-white"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Persona
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Preview Generated Persona */}
            {step === "preview" && generatedPersona && (
              <div className="space-y-4">
                {/* Persona Preview Card */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  {/* Header */}
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-white/10">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/30 to-divine-red/30 flex items-center justify-center">
                      <span className="text-2xl font-cinzel text-white">
                        {generatedPersona.name?.[0] || "?"}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-cinzel text-xl text-white">{generatedPersona.name}</h3>
                      <p className="text-white/60 text-sm">
                        {generatedPersona.age && `${generatedPersona.age} years old`}
                        {generatedPersona.living_location && ` • ${generatedPersona.living_location}`}
                      </p>
                    </div>
                  </div>

                  {/* Field Count Summary */}
                  <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <p className="text-purple-300 text-sm">
                      ✓ Generated {Object.keys(generatedPersona).filter(k => generatedPersona[k] && generatedPersona[k] !== "").length} fields across all sections
                    </p>
                  </div>

                  {/* Scrollable Preview Sections */}
                  <div className="max-h-[400px] overflow-y-auto space-y-4 pr-2">
                    {/* Identity & Basics */}
                    <div className="space-y-2">
                      <h4 className="text-divine-gold text-sm font-semibold border-b border-white/10 pb-1">Identity & Basics</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {generatedPersona.sex && <div><span className="text-white/40">Sex:</span> <span className="text-white">{generatedPersona.sex}</span></div>}
                        {generatedPersona.pronouns && <div><span className="text-white/40">Pronouns:</span> <span className="text-white">{generatedPersona.pronouns}</span></div>}
                        {generatedPersona.ethnicity && <div><span className="text-white/40">Ethnicity:</span> <span className="text-white">{generatedPersona.ethnicity}</span></div>}
                        {generatedPersona.sexual_orientation && <div><span className="text-white/40">Orientation:</span> <span className="text-white">{generatedPersona.sexual_orientation}</span></div>}
                        {generatedPersona.body_type && <div><span className="text-white/40">Body Type:</span> <span className="text-white">{generatedPersona.body_type}</span></div>}
                        {generatedPersona.height && <div><span className="text-white/40">Height:</span> <span className="text-white">{generatedPersona.height}</span></div>}
                      </div>
                    </div>

                    {/* Work & Career */}
                    {(generatedPersona.work_details || generatedPersona.education_history) && (
                      <div className="space-y-2">
                        <h4 className="text-divine-gold text-sm font-semibold border-b border-white/10 pb-1">Work & Career</h4>
                        {generatedPersona.work_details && <p className="text-white/80 text-sm">{generatedPersona.work_details}</p>}
                        {generatedPersona.education_history && <p className="text-white/60 text-sm">{generatedPersona.education_history}</p>}
                      </div>
                    )}

                    {/* Interests & Lifestyle */}
                    {(generatedPersona.hobbies_passions || generatedPersona.fantasies_dreams) && (
                      <div className="space-y-2">
                        <h4 className="text-divine-gold text-sm font-semibold border-b border-white/10 pb-1">Interests & Lifestyle</h4>
                        {generatedPersona.hobbies_passions && <p className="text-white/80 text-sm"><span className="text-white/40">Hobbies:</span> {generatedPersona.hobbies_passions}</p>}
                        {generatedPersona.fantasies_dreams && <p className="text-white/80 text-sm"><span className="text-white/40">Dreams:</span> {generatedPersona.fantasies_dreams}</p>}
                      </div>
                    )}

                    {/* Relationship Style */}
                    {(generatedPersona.attachment_style || generatedPersona.primary_love_lang || generatedPersona.communication_pref_style) && (
                      <div className="space-y-2">
                        <h4 className="text-divine-gold text-sm font-semibold border-b border-white/10 pb-1">Relationship Style</h4>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {generatedPersona.attachment_style && <div><span className="text-white/40">Attachment:</span> <span className="text-white">{generatedPersona.attachment_style.replace(/_/g, ' ')}</span></div>}
                          {generatedPersona.primary_love_lang && <div><span className="text-white/40">Love Language:</span> <span className="text-white">{generatedPersona.primary_love_lang.replace(/_/g, ' ')}</span></div>}
                          {generatedPersona.communication_pref_style && <div><span className="text-white/40">Communication:</span> <span className="text-white">{generatedPersona.communication_pref_style}</span></div>}
                          {generatedPersona.conflict_style && <div><span className="text-white/40">Conflict Style:</span> <span className="text-white">{generatedPersona.conflict_style.replace(/_/g, ' ')}</span></div>}
                          {generatedPersona.social_energy && <div><span className="text-white/40">Social Energy:</span> <span className="text-white">{generatedPersona.social_energy}</span></div>}
                          {generatedPersona.emotional_openness && <div><span className="text-white/40">Emotional Openness:</span> <span className="text-white">{generatedPersona.emotional_openness.replace(/_/g, ' ')}</span></div>}
                        </div>
                      </div>
                    )}

                    {/* Values & Beliefs */}
                    {(generatedPersona.political_views || generatedPersona.spiritual_views) && (
                      <div className="space-y-2">
                        <h4 className="text-divine-gold text-sm font-semibold border-b border-white/10 pb-1">Values & Beliefs</h4>
                        {generatedPersona.political_views && <p className="text-white/80 text-sm"><span className="text-white/40">Political:</span> {generatedPersona.political_views}</p>}
                        {generatedPersona.spiritual_views && <p className="text-white/80 text-sm"><span className="text-white/40">Spiritual:</span> {generatedPersona.spiritual_views}</p>}
                      </div>
                    )}

                    {/* Full Profile Notes */}
                    {generatedPersona.notes && (
                      <div className="space-y-2">
                        <h4 className="text-divine-gold text-sm font-semibold border-b border-white/10 pb-1">Full Profile</h4>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">{generatedPersona.notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setGeneratedPersona(null);
                      setStep("generate");
                    }}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRegen}
                    disabled={generating}
                    className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                  >
                    {generating ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Regenerate
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="flex-1 bg-divine-gold hover:bg-divine-gold-light text-black"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save & Edit
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AddPersonaModal;
